import { getInvoicePartyName } from "./invoice";

const resolveItemKey = (item) => {
  const id = item?.itemId?._id || item?.itemId;
  if (id) return String(id);
  return `${item?.name || "item"}-${item?.unit || ""}`;
};

const isShoppingUnit = (unit) => String(unit || "").trim().toUpperCase() === "SH";

const normalizeUnix = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
};

export const formatReportTimestamp = (record) => {
  let unix = normalizeUnix(record?.date);
  if (!unix && record?.createdAt) {
    unix = Math.floor(new Date(record.createdAt).getTime() / 1000);
  }
  if (!unix) return "—";
  return new Date(unix * 1000).toLocaleString("en-PK", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const getRecordUnix = (record) => {
  const fromDate = normalizeUnix(record?.date);
  if (fromDate) return fromDate;
  if (record?.createdAt) {
    return Math.floor(new Date(record.createdAt).getTime() / 1000);
  }
  return 0;
};

/** Customer sales + customer returns + customer NPI (credit/debit). Returns & NPI debit subtract. */
export const buildCustomerItemSummary = (
  salesInvoices = [],
  returnInvoices = [],
  npiInvoices = []
) => {
  const byCustomer = new Map();

  const ensureGroup = (customerId, customerName, shoppingWala = "") => {
    const customerKey = String(customerId);
    if (!byCustomer.has(customerKey)) {
      byCustomer.set(customerKey, {
        customer_id: customerId,
        customer_name: customerName,
        shopping_wala: shoppingWala,
        is_guest: false,
        invoice_count: 0,
        sale_count: 0,
        return_count: 0,
        npi_credit_count: 0,
        npi_debit_count: 0,
        total_amount: 0,
        shopping_amount: 0,
        npi_credit_total: 0,
        npi_debit_total: 0,
        items: new Map(),
        invoice_records: [],
      });
    }
    return byCustomer.get(customerKey);
  };

  const processInvoice = (inv, isReturn) => {
    const customerId = inv.customerId?._id || inv.customerId || null;
    if (!customerId) return;

    const customerName = getInvoicePartyName(inv);
    const sign = isReturn ? -1 : 1;
    const group = ensureGroup(
      customerId,
      customerName,
      String(inv.customerId?.ref ?? "").trim()
    );

    group.invoice_count += 1;
    if (isReturn) group.return_count += 1;
    else group.sale_count += 1;
    group.total_amount += sign * Number(inv.total_amount || 0);

    group.invoice_records.push({
      id: inv._id,
      invoice_no: inv.invoice_no || "—",
      auto_invoice_no: inv.auto_invoice_no ?? "",
      page_no: String(inv.customerId?.page_no ?? "").trim(),
      is_return: isReturn,
      is_npi_credit: false,
      is_npi_debit: false,
      total_amount: Number(inv.total_amount || 0),
      timestamp: formatReportTimestamp(inv),
      date_unix: getRecordUnix(inv),
    });

    (inv.items || []).forEach((item) => {
      const itemKey = resolveItemKey(item);
      if (!group.items.has(itemKey)) {
        group.items.set(itemKey, {
          itemId: item?.itemId?._id || item?.itemId || itemKey,
          name: item.name || "-",
          unit: item.unit || "-",
          qty: 0,
          amount: 0,
        });
      }
      const row = group.items.get(itemKey);
      const qty = sign * Number(item.qty || 0);
      const amount = sign * Number(item.amount || 0);
      row.qty += qty;
      row.amount += amount;
      if (isShoppingUnit(item.unit)) {
        group.shopping_amount += amount;
      }
    });
  };

  const processNpi = (row) => {
    if (!row || row.isDeleted === true) return;
    if (Number(row.user_type) !== 1) return; // customer only
    const customerId = row.customerId?._id || row.customerId || null;
    if (!customerId) return;

    const amt = Number(row.amount || 0);
    if (!Number.isFinite(amt) || amt === 0) return;

    const isCredit = Number(row.invoice_type) === 1;
    const customerName =
      row.customer_name ||
      row.customerId?.name ||
      row.party_name ||
      "Customer";
    const group = ensureGroup(
      customerId,
      customerName,
      String(row.customerId?.ref ?? "").trim()
    );

    group.invoice_count += 1;
    if (isCredit) {
      group.npi_credit_count += 1;
      group.npi_credit_total += amt;
      group.total_amount += amt; // CR — same as sale
    } else {
      group.npi_debit_count += 1;
      group.npi_debit_total += amt;
      group.total_amount -= amt; // DR — same as return/payment
    }

    group.invoice_records.push({
      id: row._id,
      invoice_no: row.invoice_no != null ? String(row.invoice_no) : "—",
      auto_invoice_no: "",
      page_no: String(row.customerId?.page_no ?? "").trim(),
      is_return: false,
      is_npi_credit: isCredit,
      is_npi_debit: !isCredit,
      total_amount: amt,
      timestamp: formatReportTimestamp(row),
      date_unix: getRecordUnix(row),
    });
  };

  salesInvoices.forEach((inv) => processInvoice(inv, false));
  returnInvoices.forEach((inv) => processInvoice(inv, true));
  (npiInvoices || []).forEach(processNpi);

  return Array.from(byCustomer.values())
    .map((group) => ({
      ...group,
      items: Array.from(group.items.values()).sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      ),
      invoice_records: group.invoice_records.sort(
        (a, b) => (b.date_unix || 0) - (a.date_unix || 0)
      ),
    }))
    .sort((a, b) => String(a.customer_name).localeCompare(String(b.customer_name)));
};

export const filterCustomerSummary = (groups, searchText) => {
  const q = searchText.trim().toLowerCase();
  if (!q) return groups;
  return groups.filter(
    (group) =>
      String(group.customer_name || "").toLowerCase().includes(q) ||
      group.items.some((item) => String(item.name || "").toLowerCase().includes(q)) ||
      (group.invoice_records || []).some(
        (rec) =>
          String(rec.invoice_no || "").toLowerCase().includes(q) ||
          String(rec.auto_invoice_no ?? "").toLowerCase().includes(q)
      )
  );
};
